import { useState } from 'react';
import { useCanAccess, useGetOne, useNotify } from 'ra-core';
import { poserUnReglageDeStructure } from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { deballer, lireLErreur } from '../../fournisseurs/client';
import { useStructureCourante } from '../../fournisseurs/structure-courante';
import { ReglageDuParc } from './ReglageDuParc';

type Reglage = {
  nom: string;
  unite: 'megaoctets' | 'jours' | 'tableaux' | 'eleves';
  valeur: number;
  defaut: number;
  plancher: number;
  plafondHaut: number | null;
  portee: 'structureDuTuteur' | 'laPersonne';
};
/**
 * **Le type local s'arrête aux cinq réglages, et `parc` n'y figure plus — D du 20260829.**
 *
 * Le contrat déclare `parc: objet | NULL`, « absent pour une personne qui tient un espace ».
 * Ce type le déclarait **facultatif**, ce qui n'est pas la même absence : `null !== undefined`,
 * donc le test qui gardait le bloc du parc était **vrai chez l'`orgadmin`**, et la première
 * lecture du champ déréférençait `null`. Le bloc lit désormais par sa propre route et se garde
 * par la matrice — voir `ReglageDuParc.tsx` —, donc ce champ n'a plus d'usage ici : le retirer
 * ferme le défaut au lieu de le corriger, et un `parc` réintroduit sans son `| null` rougirait.
 */
type Reponse = {
  structureId: string;
  reglages: Reglage[];
  plafondsDeVolumeSeCumulent: boolean;
  personnesAuPlafondDeVolume: number;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **UN SEUL ÉCRAN PORTE LES CINQ RÉGLAGES — F-65, ET C'EST SA SIMPLIFICATION.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-65 : « **UN SEUL ÉCRAN PORTE LES CINQ RÉGLAGES.** Les quatre écrans de réglage rendus par
 * quatre domaines, plus celui du plafond d'élèves, **sont le même écran** — **la simplification
 * la plus nette de ce document**. »
 *
 * « **Ce qu'il montre par réglage** : sa **valeur courante**, sa **valeur par défaut**, et ses
 * **bornes**. » Les trois viennent du serveur, réglage par réglage : les recopier ici ferait
 * **cinq valeurs par défaut et dix bornes** dans le front, et « la liste est **FERMÉE À
 * CINQ**, et **un sixième réglage de structure est une livraison de code** » — le jour de cette
 * livraison, une copie oubliée mentirait sans rougir.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE RÉGLAGE DU PARC EST SUR CE MÊME ÉCRAN, ET IL A CESSÉ D'EN DÉPENDRE — 20260829.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-65 / DD-61 : « **un bloc « réglage du parc » sur ce même écran, visible du compte qui voit
 * tout SEUL, fermé à la structure par refus explicite. Aucun écran nouveau n'est créé.** »
 *
 * « **Il PLAFONNE le réglage d'élèves d'une structure sans le remplacer.** L'`orgadmin` continue
 * de régler le sien, **et le réglage effectif d'un cours est le plus bas des deux.** » Critère 1 :
 * « QUAND un cours est créé, LE SYSTÈME DOIT retenir comme plafond d'élèves **le plus bas** du
 * réglage de la structure et du réglage du parc. »
 *
 * « **Ce n'est pas un sixième réglage de structure** : c'est un **nombre du produit**, porté par
 * personne d'autre, du même genre que le seuil de six flux d'image. **La liste fermée à cinq
 * tient sans changement.** »
 *
 * **CE FICHIER EST DONC EN DEUX MORCEAUX, ET LA COUPURE PORTE CETTE PHRASE.** Les cinq réglages
 * appartiennent à une structure : sans structure courante, il n'y a rien à afficher, et
 * `ReglagesDeLaStructure` le dit. **Le réglage du parc n'appartient à aucune structure**, et il
 * était pourtant gardé derrière le même choix — un nombre du parc inatteignable tant qu'une
 * structure quelconque n'était pas prise dans le sélecteur. `ReglageDuParc` se rend donc **hors
 * de cette garde**, en lisant `GET /api/v1/admin/parc/reglage`, qui ne prend aucune structure
 * dans son adresse. Le motif complet, la confrontation des deux sources et ce que le choix
 * coûte sont dans ce fichier-là ; **aucun écran nouveau n'est créé** — c'est le même écran, et
 * `reglages/index.ts` n'exporte toujours qu'un composant.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **DEUX CHOSES QUE L'ÉCRAN DOIT DIRE, ET QU'AUCUN CHAMP NE PORTE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * 1. « **L'écran dit que les plafonds de volume se CUMULENT sur les rattachements d'une
 *    personne**, sans quoi une structure croirait régler seule ce que ses personnes ont. **C'est
 *    le patron de portée propre à ce réglage : il gouverne une personne, là où les quatre autres
 *    gouvernent un cours.** »
 * 2. Le **coût écrit et non corrigé** : « une structure règle son plafond de volume **à
 *    l'aveugle**, ne voyant ni le contenu ni le volume des espaces. **Le seul chiffre qu'elle en
 *    a est le nombre de personnes au plafond.** » Ce chiffre est donc affiché — c'est la seule
 *    chose que le corpus lui accorde.
 *
 * **Et une troisième, qui vaut pour les deux blocs** : « **une modification ne touche jamais un
 * cours en train de se dérouler** » (critère 4), et « une **baisse ne touche pas un cours en
 * train de se dérouler** » pour le parc. Cas de bord 4 : « deux personnes modifient le même
 * réglage en même temps → **le dernier gagne, aucun verrou**, et les deux modifications laissent
 * chacune leur ligne. »
 */

const LIBELLES: Readonly<Record<string, string>> = {
  poidsMaximumDunTableau: 'Poids maximum d’un tableau',
  dureeDeRelectureDuneScene: 'Durée de relecture d’une scène',
  nombreMaximumDeTableauxDunCours: 'Nombre maximum de tableaux d’un cours',
  plafondDeVolumeDunEspace: 'Plafond de volume d’un espace de fichiers',
  nombreMaximumDelevesParCours: 'Nombre maximum d’élèves par cours',
};

const UNITES: Readonly<Record<string, string>> = {
  megaoctets: 'Mo',
  jours: 'jours',
  tableaux: 'tableaux',
  eleves: 'élèves',
};

/**
 * **La coquille de l'écran, et elle ne lit rien.**
 *
 * Elle porte le titre et l'ordre des deux blocs. Chacun se garde lui-même — l'un par la
 * structure courante, l'autre par la matrice —, ce qui est la seule façon d'avoir un bloc qui
 * survit à l'absence de l'autre sans qu'un `if` d'écran arbitre entre eux.
 */
export function EcranDesReglages() {
  return (
    <main className="flex max-w-3xl flex-col gap-6 p-6">
      <h2 className="text-lg font-medium">Réglages</h2>
      <ReglagesDeLaStructure />
      <ReglageDuParc />
    </main>
  );
}

/** **Les cinq, et eux seuls.** Ils appartiennent à une structure : sans elle, rien à montrer. */
function ReglagesDeLaStructure() {
  const { structureId, selecteurAffiche } = useStructureCourante();
  const { canAccess: peutEcrire } = useCanAccess({ resource: 'reglages', action: 'edit' });
  const notifier = useNotify();
  const [enCours, setEnCours] = useState<string | null>(null);

  const { data, isPending, error, refetch } = useGetOne<Reponse & { id: string }>(
    'reglages',
    { id: structureId ?? '' },
    { enabled: structureId !== null },
  );

  if (structureId === null) {
    return (
      <Alert>
        <AlertTitle>Choisissez une structure.</AlertTitle>
        <AlertDescription>
          {selecteurAffiche
            ? 'Les cinq réglages appartiennent à une structure. Prenez-en une dans le ' +
              'sélecteur en tête de la surface.'
            : 'Aucun rattachement actif n’est associé à ce compte.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error !== null || data === undefined) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Les réglages n’ont pas pu être lus.</AlertTitle>
        <AlertDescription>
          {lireLErreur(error).message}{' '}
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Réessayer
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const poser = async (evenement: React.FormEvent<HTMLFormElement>, reglage: Reglage) => {
    evenement.preventDefault();
    const saisie = new FormData(evenement.currentTarget).get('valeur');
    const valeur = Number(saisie);
    if (!Number.isInteger(valeur)) {
      notifier('La valeur doit être un nombre entier.', { type: 'warning' });
      return;
    }
    setEnCours(reglage.nom);
    try {
      await deballer(
        poserUnReglageDeStructure({
          path: { id: structureId, nom: reglage.nom },
          body: { valeur },
        }),
      );
      notifier('Réglage enregistré. La modification laisse une ligne.', { type: 'info' });
      await refetch();
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      // **Le refus DIT la borne**, F-65 : « aucune valeur hors bornes n'est acceptée, et le
      // refus DIT la borne », et le critère 2 exige que « le corps **porte la borne
      // franchie** ». Le message du serveur est donc affiché tel quel — le reformuler ici
      // perdrait précisément le nombre que le critère fait porter.
      notifier(lue.message, { type: 'error' });
    } finally {
      setEnCours(null);
    }
  };

  return (
    <>
      <p className="text-muted-foreground text-sm">
        Cinq réglages, et la liste est fermée : un sixième est une livraison de
        code. Une modification ne touche jamais un cours en train de se
        dérouler, et chacune laisse sa ligne. Deux modifications simultanées :
        la dernière gagne, il n’y a aucun verrou.
      </p>

      <div className="flex flex-col gap-5">
        {data.reglages.map((reglage) => (
          <form
            key={reglage.nom}
            className="flex flex-col gap-1"
            onSubmit={(e) => void poser(e, reglage)}
          >
            <Label htmlFor={reglage.nom}>
              {LIBELLES[reglage.nom] ?? reglage.nom}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id={reglage.nom}
                name="valeur"
                type="number"
                defaultValue={reglage.valeur}
                min={reglage.plancher}
                {...(reglage.plafondHaut !== null
                  ? { max: reglage.plafondHaut }
                  : {})}
                className="w-32"
                disabled={!peutEcrire}
              />
              <span className="text-muted-foreground text-sm">
                {UNITES[reglage.unite]}
              </span>
              {peutEcrire && (
                <Button
                  type="submit"
                  size="sm"
                  disabled={enCours === reglage.nom}
                >
                  Enregistrer
                </Button>
              )}
            </div>
            <span className="text-muted-foreground text-xs">
              Par défaut {reglage.defaut} {UNITES[reglage.unite]} — plancher{' '}
              {reglage.plancher}
              {reglage.plafondHaut !== null
                ? `, plafond ${reglage.plafondHaut}`
                : ', aucune borne haute'}
              .
              {/* **Le patron de portée propre à ce réglage**, et l'écran doit le dire : il
                  gouverne UNE PERSONNE là où les quatre autres gouvernent un cours. */}
              {reglage.portee === 'laPersonne' && data.plafondsDeVolumeSeCumulent && (
                <>
                  {' '}
                  Ce plafond gouverne une personne et non un cours : il{' '}
                  <strong>se cumule sur ses rattachements</strong>. Vous ne voyez ni le contenu
                  ni le volume des espaces — le seul chiffre disponible est le nombre de
                  personnes au plafond, {data.personnesAuPlafondDeVolume}.
                </>
              )}
              {/* **La borne haute de ce réglage EST le réglage du parc**, et c'est le
                  serveur qui le dit : `plafondHaut` vaut ici `parc.maxStudentsPerCourse`.
                  La phrase se déclenche donc sur ce que ce réglage porte, et non sur la
                  présence d'un bloc réservé à l'autre rôle — l'`orgadmin` doit la lire, lui
                  qui subit le plafond sans jamais voir le nombre du parc. */}
              {reglage.nom === 'nombreMaximumDelevesParCours' && reglage.plafondHaut !== null && (
                <>
                  {' '}
                  Cette borne haute est <strong>le réglage du parc</strong> : il plafonne le
                  vôtre sans le remplacer, et le plafond effectif d’un cours est le plus bas
                  des deux.
                </>
              )}
            </span>
          </form>
        ))}
      </div>
    </>
  );
}
